import { GetStaticProps } from 'next';
import { Router } from 'next/router';
import React, { useEffect } from 'react';

import Controller from 'components/Controller/Controller';
import useOverElement from 'hooks/useOverElement';
import { useCopyStore, useGlobalStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { workPageQuery } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { ROUTES } from 'utils/routes';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { pageMotionProps } from 'utils/styles/animations';

import { DataCMSTypes, WorkPageDataCMS } from './WorkPage.types';

import * as S from './WorkPage.styles';

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const id = await marketPageId(MarketPages.workPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS<DataCMSTypes>({
    query: workPageQuery({ id, locale, globalIds }),
  });

  const {
    data: {
      sharedCopies,
      workPage: page,
      redirectPopups,
      cookieBar,
      navbar,
      footer,
    },
  } = response;

  if (!page || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: page?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      page,
    },
    revalidate: ISR_TIMEOUT,
  };
};

interface WorkPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  page: WorkPageDataCMS;
  router: Router;
}

const WorkPage: React.FunctionComponent<WorkPageProps> = ({
  initialCopy,
  page,
}) => {
  const { setCopy } = useCopyStore();
  const { setHeaderText } = useGlobalStore();

  const [ref] = useOverElement();

  useEffect(() => {
    setCopy(initialCopy);
    setHeaderText({
      default: initialCopy.navbar.navLinks.find(
        ({ href }) => href === ROUTES.WORK
      )?.headerText,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  if (!page) return null;

  return (
    <S.Wrapper {...pageMotionProps}>
      <S.HeadlineWrapper ref={ref}>
        <S.WorkPageHeadline text={page?.headline} />
      </S.HeadlineWrapper>

      <S.WorkPageGridTiles projects={page?.projects} />

      <Controller
        filterText={page?.buttonText}
        filterTagsList={page?.filterPopup}
        viewChanger={false}
      />
    </S.Wrapper>
  );
};

export default WorkPage;
